// src/features/campaigns/campaignsApiSlice.js
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

// --- Normalized adapter ---
const campaignsAdapter = createEntityAdapter({
  // use our own string "id" we set in transformResponse
  selectId: (c) => c.id,
});

// Keep a stable extra field for pagination metadata
const initialState = campaignsAdapter.getInitialState({ total: 0 });

export const campaignsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCampaigns: builder.query({
        // args: { page, limit, q, status, sortBy, order, ... }
        query: (params = {}) => ({ url: '/campaigns', params }),

        transformResponse: (response, meta) => {
          let items = [];
          let total = 0;

          if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
            items = response.items ?? [];
            total = Number(response.total ?? 0);
          } else if (Array.isArray(response)) {
            items = response;
            const headerTotal =
              meta?.response?.headers?.get('X-Total-Count') ??
              meta?.response?.headers?.get('x-total-count');
            total = headerTotal ? Number(headerTotal) : items.length;
          }

          // 1) Force a *single* stable string key, and set BOTH id and _id to it
          const processed = items.map((c) => {
            const raw = c?._id ?? c?.id;
            const sid = raw != null ? String(raw) : undefined;
            return { ...c, id: sid, _id: sid };
          });

          // 2) Manual normalization using *id* as the key (matches adapter.selectId)
          const ids = [];
          const entities = {};
          for (const c of processed) {
            if (c.id) {
              ids.push(c.id);
              entities[c.id] = c;
            }
          }

          // Optional debug (remove after verifying)
          // console.log('normalized (id):', { idsLen: ids.length, total, first: ids[0] });

          return { ids, entities, total };
        },
      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'Campaign', id })),
              { type: 'Campaign', id: 'LIST' }, // ⬅️ use LIST consistently
            ]
          : [{ type: 'Campaign', id: 'LIST' }],
    }),

    getCampaignById: builder.query({
      query: (id) => `/campaigns/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'Campaign', id }],
    }),

    addNewCampaign: builder.mutation({
      query: (body) => ({ url: '/campaigns', method: 'POST', body }),
      invalidatesTags: [{ type: 'Campaign', id: 'LIST' }], // ⬅️ matches providesTags
    }),

    updateCampaign: builder.mutation({
      query: ({ id, ...patch }) => ({ url: `/campaigns/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
    }),

    deleteCampaign: builder.mutation({
      query: ({ id }) => ({ url: `/campaigns/${id}`, method: 'DELETE', body: { id } }),
      invalidatesTags: [{ type: 'Campaign', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCampaignsQuery,
  useGetCampaignByIdQuery,
  useAddNewCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
} = campaignsApiSlice;

/* -----------------------
   Arg-aware selectors
   ----------------------- */

// Base result selector factory (must pass the same args you use in the hook)
const selectGetCampaignsResult = campaignsApiSlice.endpoints.getCampaigns.select;

export const makeSelectCampaignsData = (args) =>
  createSelector(selectGetCampaignsResult(args), (res) => res?.data ?? initialState);

// Export a factory that returns normalized selectors + total for a given args set
export const makeCampaignSelectors = (args) => {
  const selectData = makeSelectCampaignsData(args);
  const base = campaignsAdapter.getSelectors((state) => selectData(state));
  return {
    selectAllCampaigns: base.selectAll,
    selectCampaignById: base.selectById,
    selectCampaignIds: base.selectIds,
    selectCampaignEntities: base.selectEntities,
    selectTotal: (state) => selectData(state).total ?? 0,
  };
};
